/**
 * PDF Viewer Component
 * Renders PDF pages using react-pdf
 * Page-level citations only - no highlighting
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, AlertCircle, RotateCcw } from 'lucide-react';

// Import react-pdf styles for text layer and annotations
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  /** PDF document URL (blob URL, data URL, or regular URL) */
  documentUrl: string;
  /** Current page to display (1-indexed) */
  currentPage: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when document loads successfully with total page count */
  onLoadSuccess?: (numPages: number) => void;
  /** Width for the PDF page (defaults to container width on mobile) */
  width?: number;
  /** Zoom level (1.0 = 100%) */
  zoom?: number;
}

export function PDFViewer({
  documentUrl,
  currentPage,
  onPageChange,
  onLoadSuccess,
  width,
  zoom = 1.0,
}: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);

  // Measure container width for responsive sizing
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // On mobile, use full width; on desktop, use provided width or measured width
        const measuredWidth = entry.contentRect.width;
        setContainerWidth(measuredWidth > 0 ? measuredWidth : undefined);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Reset state when document URL changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    setNumPages(0);
    setRetryCount(0);
  }, [documentUrl]);

  const handleLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setLoading(false);
      setError(null);
      onLoadSuccess?.(numPages);
    },
    [onLoadSuccess]
  );

  const handleLoadError = useCallback((err: Error) => {
    console.error('PDF load error:', err);
    setError('Failed to load PDF document');
    setLoading(false);
  }, []);

  const handleRetry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    setLoading(true);
    setError(null);
  }, []);

  // Calculate effective width
  const effectiveWidth = width || (containerWidth ? Math.min(containerWidth - 32, 800) : undefined);

  if (!documentUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-gray-500 dark:text-gray-400 text-sm">No document selected</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center w-full h-full overflow-auto bg-gray-50 dark:bg-gray-900/40 p-4"
    >
      {error ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle size={24} />
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
          >
            <RotateCcw size={16} />
            Retry
          </button>
        </div>
      ) : (
        <div className="pdf-document-container bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
          <Document
            key={`${documentUrl}-${retryCount}`}
            file={documentUrl}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={handleLoadError}
            loading={
              <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 py-24 px-12">
                <Loader2 className="animate-spin" size={20} />
                <span className="text-sm">Loading PDF...</span>
              </div>
            }
          >
            <Page
              pageNumber={currentPage}
              width={effectiveWidth ? effectiveWidth * zoom : undefined}
              scale={effectiveWidth ? undefined : zoom}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              loading={
                <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 py-24 px-12">
                  <Loader2 className="animate-spin" size={20} />
                  <span className="text-sm">Loading page {currentPage}...</span>
                </div>
              }
            />
          </Document>
        </div>
      )}

      {/* Loading overlay for page changes */}
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 dark:bg-gray-900/80">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 px-4 py-2 rounded-xl shadow">
            <Loader2 className="animate-spin" size={20} />
            <span className="text-sm">Loading document...</span>
          </div>
        </div>
      )}

      <style>{`
        .pdf-document-container {
          max-width: 100%;
        }
        .react-pdf__Page {
          max-width: 100%;
        }
        .react-pdf__Page__canvas {
          max-width: 100%;
          height: auto !important;
        }
        .react-pdf__Page__textContent {
          user-select: text;
        }
      `}</style>
    </div>
  );
}

export default PDFViewer;
